import React from 'react';
import { X, RotateCcw, Send, Sparkles } from 'lucide-react';
import { Textarea } from '@/components/ui/textarea';
import { Message } from '../../types/assistant';

interface LabWindowProps {
  messages: Message[];
  inputMessage: string;
  isLoading: boolean | undefined;
  isSending: boolean | undefined;
  contextualRecipe?: { title: string } | null;
  onInputChange: (value: string) => void;
  onSendMessage: () => void;
  onKeyPress: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onReset: () => void;
  onClose: () => void;
  onInput: (e: React.FormEvent<HTMLTextAreaElement>) => void;
  inputRef: React.RefObject<HTMLTextAreaElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  renderMessageContent: (message: Message) => React.ReactNode;
  onQuickAction?: (action: 'paste-recipe' | 'create-recipe') => void;
}

export const LabWindow: React.FC<LabWindowProps> = ({
  messages,
  inputMessage,
  isLoading,
  isSending,
  contextualRecipe,
  onInputChange,
  onSendMessage,
  onKeyPress,
  onReset,
  onClose,
  onInput,
  inputRef,
  messagesEndRef,
  renderMessageContent,
  onQuickAction
}) => {
  return (
    <div className="fixed inset-0 z-[999]">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Lab Window Container */}
      <div className="absolute inset-x-0 bottom-0 md:inset-auto md:bottom-6 md:right-6 md:w-[440px] md:max-w-[calc(100vw-3rem)] max-h-[90vh] md:max-h-[640px] bg-white dark:bg-[#1A1714] md:rounded-2xl shadow-2xl border-0 md:border border-stone-200 dark:border-stone-700 overflow-hidden flex flex-col animate-fadeInUp">
        
        {/* Header */}
        <div className="relative px-4 py-3 border-b border-stone-100 dark:border-stone-800 bg-white dark:bg-[#1A1714]">
          {/* Subtle gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-r from-terracotta-500/5 to-sage-500/5" />
          
          <div className="relative flex items-center justify-between">
            <div className="flex items-center gap-3">
              {/* Avatar with status */}
              <div className="relative">
                <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-terracotta-200 dark:border-terracotta-800">
                  <img
                    src="/pers4.jpg"
                    alt="Lab Assistant"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                {/* Status dot */}
                <div className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white dark:border-[#1A1714] ${
                  isLoading ? 'bg-terracotta-400' : 'bg-sage-400'
                }`} />
              </div>
              
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-stone-900 dark:text-white text-sm">Lab Assistant</span>
                  {contextualRecipe && (
                    <span className="text-xs text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 px-2 py-0.5 rounded-full truncate max-w-[120px]">
                      {contextualRecipe.title}
                    </span>
                  )}
                </div>
                <span className="text-xs text-stone-500 dark:text-stone-400">
                  {isLoading ? 'Thinking...' : 'Ready to help'}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-1">
              <button
                onClick={onReset}
                className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-colors"
                title="New Conversation"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300 hover:bg-stone-100 dark:hover:bg-stone-800 rounded-full transition-colors"
                title="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-stone-50/50 dark:bg-stone-800/30 chat-scroll">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.isUser ? 'justify-end' : 'justify-start'} ${
                msg.isUser ? 'message-user' : 'message-assistant'
              }`}
            >
              <div
                className={`max-w-[88%] text-sm whitespace-pre-line ${
                  msg.metadata?.type === 'editable-prompt'
                    ? 'w-full bg-white dark:bg-stone-800 rounded-xl shadow-sm border border-stone-200 dark:border-stone-700 p-3'
                    : msg.isUser
                      ? 'bg-gradient-to-br from-terracotta-500 to-terracotta-600 text-white rounded-2xl rounded-br-md shadow-md px-4 py-2.5'
                      : 'bg-white dark:bg-stone-800 text-stone-900 dark:text-white rounded-2xl rounded-bl-md shadow-sm border border-stone-100 dark:border-stone-700 px-4 py-2.5'
                } ${
                  msg.content.toLowerCase().startsWith('error:')
                    ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800'
                    : ''
                }`}
              >
                {renderMessageContent(msg)}
              </div>
            </div>
          ))}

          {/* Loading indicator */}
          {isLoading && (
            <div className="flex justify-start message-assistant">
              <div className="bg-white dark:bg-stone-800 px-4 py-3 rounded-2xl rounded-bl-md shadow-sm border border-stone-100 dark:border-stone-700">
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 bg-stone-400 dark:bg-stone-500 rounded-full typing-dot-1" />
                  <span className="w-2 h-2 bg-stone-400 dark:bg-stone-500 rounded-full typing-dot-2" />
                  <span className="w-2 h-2 bg-stone-400 dark:bg-stone-500 rounded-full typing-dot-3" />
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="px-4 py-3 border-t border-stone-100 dark:border-stone-800 bg-white dark:bg-[#1A1714] safe-area-pb">
          <div className="flex gap-2 items-end">
            <div className="flex-1 relative">
              <Textarea
                ref={inputRef}
                placeholder="Ask about recipes, paste a recipe to import..."
                value={inputMessage}
                onChange={(e) => onInputChange(e.target.value)}
                onKeyDown={onKeyPress}
                onInput={onInput}
                className="w-full px-4 py-2.5 rounded-2xl bg-stone-100 dark:bg-stone-800 border-0 text-sm focus:ring-2 focus:ring-terracotta-400 outline-none resize-none overflow-hidden min-h-[44px] max-h-[120px] placeholder:text-stone-400 dark:placeholder:text-stone-500"
                style={{
                  height: 'auto',
                  minHeight: '44px',
                  maxHeight: '120px',
                }}
                rows={1}
                disabled={isSending}
              />
            </div>
            <button
              onClick={onSendMessage}
              disabled={isSending || !inputMessage.trim()}
              className="p-2.5 rounded-xl bg-terracotta-500 hover:bg-terracotta-600 text-white disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-terracotta-500/25 transition-all flex-shrink-0 active:scale-95"
              aria-label="Send"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          
          {/* Quick hints */}
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1 scrollbar-hide">
            <button
              onClick={() => onQuickAction?.('create-recipe')}
              className="flex-shrink-0 text-xs text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 px-3 py-1.5 rounded-full transition-colors"
            >
              <Sparkles className="w-3 h-3 inline mr-1" />
              Create recipe
            </button>
            <button
              onClick={() => onQuickAction?.('paste-recipe')}
              className="flex-shrink-0 text-xs text-stone-500 dark:text-stone-400 bg-stone-100 dark:bg-stone-800 hover:bg-stone-200 dark:hover:bg-stone-700 px-3 py-1.5 rounded-full transition-colors"
            >
              Paste recipe
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
